import { useState } from 'react';
import FileIcon from '../assets/file-icon.svg';
import UploadingIcon from '../assets/data-upload-uploading.svg';
import SuccessIcon from '../assets/data-upload-success.svg';
import ErrorIcon from '../assets/data-upload-x-mark.svg';
import LoaderIcon from '../assets/data-upload-loader.svg';
import RefreshIcon from '../assets/refresh-icon.svg';
import WarningIcon from '../assets/data-upload-warning.svg';
import UploadResultsModal from '../data upload/UploadResultsModal';
import { FileUploadResult, UploadedFileData } from '../../interfaces/UploadedFileDataInterface';
import { UploadedFileStatus } from '../../interfaces/UploadedFileDataInterface';
import useUserDetails from '../hooks/useUserDetails';
import { USER_ROLES, XQ_SUPPORT_EMAIL } from '../../constants';
import { useAuditLogTableContext } from '../hooks/useAuditLogTableContext';
import FileTypesValues from '../../interfaces/FileType';
import CustomButton from './CustomButton';
import { Box, LinearProgress, LinearProgressProps, Typography } from '@mui/material';
import useUploadCardData from '../hooks/useUploadCardData';
import useUploadResults from '../hooks/useUploadResults';
import { postRequestUploadUrl, uploadFileToS3Bucket } from '../../services/uploadService';
import { UserDetails } from '../../interfaces/UserDetailsInterface';
import { AxiosProgressEvent } from 'axios';
import useErrorModal from '../hooks/useErrorModal';
import { DATA_UPLOAD_REDUCER_ACTIONS } from '../data upload/dataUploadReducer';
import { AxiosError } from 'axios';

import './FileUploadStatus.css';

interface FileUploadStatusProps {
  fileId: string;
  fileName: string;
  fileSize: string;
  fileStatus: keyof typeof UploadedFileStatus;
  fileMetadata: UploadedFileData['user_provided_file_metadata'];
  results: FileUploadResult[];
  missingColumns: string[];
  statistics: string[];
  onResubmit: (value: string) => void;
  fileType: FileTypesValues;
  uploadProgress: number;
}

function FileUploadStatus({ fileId, fileName, fileSize, fileStatus, fileMetadata, results, missingColumns, statistics, onResubmit, fileType, uploadProgress }: FileUploadStatusProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { handleOpenAuditLog } = useAuditLogTableContext();

  const { userDetails: { data: userDetails }} = useUserDetails();
  const userRole = userDetails?.role;
  const isViewOnly = userRole === USER_ROLES.ACCOUNT_MANAGER;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleResubmit = () => {
    onResubmit(fileId);
    handleCloseModal();
  };

  const renderStatusIcon = () => {
    switch (fileStatus) {
    case UploadedFileStatus.UPLOAD:
      return UploadingIcon;
    case UploadedFileStatus.SUCCESS:
    case UploadedFileStatus.DATA_ERRORS:
      return FileIcon;
    case UploadedFileStatus.MALFORMED:
    case UploadedFileStatus.UPLOAD_FAILED:
      return WarningIcon;
    default:
      return LoaderIcon;
    }
  };
  
  return (
    <div className="uploaded-file">
      <div className="file-upload-info">
        <img src={renderStatusIcon()} alt="status icon" className="upload-status-icon" />
        <div className="file-name">
          {fileName}
        </div>
        <FileStatus 
          fileStatus={fileStatus} 
          handleClick={handleOpenModal} 
          fileSize={fileSize} 
          handleReplace={handleResubmit} 
          handleOpenAuditLog={() => handleOpenAuditLog(fileType)}
          isViewOnly={isViewOnly}
          fileId={fileId}
          userDetails={userDetails}
        />
        {isModalOpen &&
          <UploadResultsModal
            fileType={fileType}
            handleCloseModal={handleCloseModal}
            handleResubmit={handleResubmit}
            results={results}
            missingColumns={missingColumns}
            statistics={statistics}
            fileMetadata={fileMetadata}
            isViewOnly={isViewOnly}
          />
        }
      </div>
      { fileStatus === UploadedFileStatus.PROCESSING && <LinearProgress /> }
      { fileStatus === UploadedFileStatus.UPLOAD && <LinearProgressWithLabel variant="determinate" value={uploadProgress} /> }
    </div>
  );
}

const fileStatusTextMap: { [key in UploadedFileStatus]: string } = {
  [UploadedFileStatus.UPLOAD]: 'Uploading...',
  [UploadedFileStatus.UPLOAD_FAILED]: 'Upload failed',
  [UploadedFileStatus.PROCESSING]: 'Processing data...',
  [UploadedFileStatus.SUCCESS]: 'No data errors detected',
  [UploadedFileStatus.DATA_ERRORS]: 'Data errors detected - Please review and/or replace file',
  [UploadedFileStatus.MALFORMED]: 'Processing failed - '
};

interface FileStatusProps {
  fileStatus: keyof typeof UploadedFileStatus;
  handleClick: () => void;
  fileSize: string;
  handleReplace: () => void;
  isViewOnly: boolean;
  handleOpenAuditLog: () => void;
  fileId: string;
  userDetails?: UserDetails;
}

export function FileStatus({ fileStatus, handleClick, fileSize, handleReplace, isViewOnly, handleOpenAuditLog, fileId, userDetails }: FileStatusProps) {
  const { uploadData, dispatch } = useUploadResults();
  const { handleUploadProgress } = useUploadCardData();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();
  
  const showActionButtons = fileStatus === UploadedFileStatus.SUCCESS || fileStatus === UploadedFileStatus.DATA_ERRORS || fileStatus === UploadedFileStatus.MALFORMED;
  const showReplaceButton = fileStatus === UploadedFileStatus.SUCCESS || fileStatus === UploadedFileStatus.DATA_ERRORS;
  const uploadSuccessStatusClass = fileStatus === UploadedFileStatus.SUCCESS ? 'upload-status-success' : '';
  const uploadErrorStatusClass = fileStatus === UploadedFileStatus.DATA_ERRORS
    || fileStatus === UploadedFileStatus.MALFORMED 
    || fileStatus === UploadedFileStatus.UPLOAD_FAILED 
    ? 'upload-status-error' : '';

  const retryUpload = async () => {
    let fileUploadData = uploadData[fileId];
    if (!fileUploadData || !userDetails) return;

    try {
      // file upload failed at requesting presigned url
      if (!fileUploadData.uploadUrl) {
        const urlResponse = await postRequestUploadUrl(userDetails.eoaId, userDetails.access_token, fileUploadData.fileMetaData);
        let fileId = urlResponse.data.s3_key.split('/').at(-1) as string;
  
        // set url for upload a file
        // save placeholder file id so it's data can be updated
        // replace placeholder file id with real one from server
        fileUploadData.uploadUrl = urlResponse.data.presigned_url;
        fileUploadData.fileIdToReplace = fileUploadData.fileMetaData.file_id;
        fileUploadData.fileMetaData.file_id = fileId;
      }

      fileUploadData.fileMetaData = {
        ...fileUploadData.fileMetaData,
        status: UploadedFileStatus.UPLOAD
      };

      if (!fileUploadData.fileIdToReplace) {
        // file has uuid from server so we only need to update the data
        dispatch({
          type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA,
          payload: fileUploadData.fileMetaData
        });
      }
      else {
        // file has placeholder id from client
        // we need to update it's id to a real one from server
        dispatch({
          type: DATA_UPLOAD_REDUCER_ACTIONS.REPLACE_UPLOADED_FILE,
          payload: { idToReplace: fileUploadData.fileIdToReplace, newFileData: fileUploadData.fileMetaData }
        });

        delete uploadData[fileUploadData.fileIdToReplace];
      }
  
      await uploadFileToS3Bucket(fileUploadData.uploadUrl, fileUploadData.file, handleUpload);
    } catch (error) {
      dispatch({ type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA, payload: fileUploadData.fileMetaData });
      handleOpenErrorModal('Error occured while uploading file!', error as AxiosError);
    }
  };

  const handleUpload = (progressEvent: AxiosProgressEvent) => {
    if (progressEvent.total) {
      const percentsCompleted = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );

      handleUploadProgress(percentsCompleted);
    }
  };

  return (
    <>
      <div data-testid="upload-info" className={`upload-info ${uploadSuccessStatusClass} ${uploadErrorStatusClass}`}>
        <div className="status-info">
          { fileStatus === UploadedFileStatus.SUCCESS && <img src={SuccessIcon} alt="success" /> }
          { fileStatus === UploadedFileStatus.DATA_ERRORS && <img src={ErrorIcon} alt="error" /> }
          <p>
            {fileStatusTextMap[fileStatus]}
            {fileStatus === UploadedFileStatus.MALFORMED ? (<a data-testid="contact-support-link" href={`mailto:${XQ_SUPPORT_EMAIL}`} className="link-color">contact support</a>) : null}
          </p>
        </div>
        { showActionButtons &&
          <>
            <CustomButton
              buttonText="Review data"
              buttonAction={handleClick}
              variant="text-underlined"
              sx={{ fontSize: 'var(--font-size-14)', padding: 0, minWidth: 0 }}
            />
            { isViewOnly ?
              <CustomButton
                buttonText="Review audit log"
                buttonAction={handleOpenAuditLog}
                variant="text-underlined"
                sx={{ fontSize: 'var(--font-size-14)', padding: 0, minWidth: 0 }}
              />
              :
              showReplaceButton ?
                <CustomButton
                  buttonText="Replace"
                  buttonAction={handleReplace}
                  variant="text-underlined"
                  sx={{ fontSize: 'var(--font-size-14)', padding: 0, minWidth: 0 }}
                />
                :
                null
            }
          </>
        }
        <div className="upload-actions">
          { fileStatus === UploadedFileStatus.UPLOAD_FAILED &&
            <CustomButton
              variant="clear"
              buttonAction={retryUpload}
              sx={{ padding: 0, minWidth: 0 }}
            >
              <img src={RefreshIcon} alt="retry" />
            </CustomButton>
          }
        </div>
        { fileSize && <p className="file-size">{fileSize}MB</p> }
      </div>
    </>
  );
}

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={{ color: 'var(--text-gray)' }}
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export default FileUploadStatus;